import { Flex, Text } from '@chakra-ui/react'
import { dashboardv99990101Client } from '@sequencehq/api/dist/clients/dashboard/v99990101'
import { useQuery } from '@sequencehq/api'
import { Currency } from '@sequencehq/api/dist/utils/commonEnums'
import {
  GreyGrey10,
  GreyGrey30,
  Lato13Regular,
  Lato16Bold
} from '@sequencehq/design-tokens'
import { format } from '@sequencehq/utils/src/dates/dateFnsPassthrough'
import { floatSafeAdd } from 'RevenueRecognition/view/utils/floatSafeAdd'
import invariant from 'tiny-invariant'
import { toMoney } from '@sequencehq/utils'
import { Skeleton } from '@sequencehq/core-components'
import { useSelectedCurrency } from 'RevenueRecognition/view/utils/useSelectedCurrency'
import { useSelectedDateRange } from 'RevenueRecognition/view/utils/useSelectedDateRange'
import { useSearchParams } from 'react-router-dom'
import { useDefaultCurrency } from 'components/CurrencySettings/useCurrencies'

const useRevRecDashboardStatsCard = (props: {
  currency?: Currency | null
  dateRange?: [Date, Date]
  customerIds?: string[]
  productIds?: string[]
}):
  | {
      isLoading: true
      data: null
    }
  | {
      isLoading: false
      data: {
        totalDeferred: string
        totalRecognized: string
        startDate: Date
        endDate: Date
      }
    } => {
  const defaultCurrency = useDefaultCurrency(!!props.currency)

  const currency = props.currency ?? defaultCurrency
  const chartsDataResponse = useQuery(
    dashboardv99990101Client.getRevRecChartsData,
    {
      // Have to put a default here otherwise typescript will be upset
      // we are checking for currency above and in the enabled check below
      currency: currency ?? 'GBP',
      startDate: props.dateRange?.[0]
        ? format(props.dateRange[0], 'yyyy-MM-dd')
        : undefined,
      endDate: props.dateRange?.[1]
        ? format(props.dateRange[1], 'yyyy-MM-dd')
        : undefined,
      ...(props.customerIds ? { customerId: props.customerIds.join(',') } : {}),
      ...(props.productIds ? { productId: props.productIds.join(',') } : {})
    },
    {
      enabled: !!currency,
      staleTime: 60000,
      select: data => {
        if (!data) {
          return
        }

        const deferredRevenueByMonth = data.period.buckets.map(
          bucket =>
            bucket.recognizedAndDeferred.ledgerAccountTotals.find(
              i => i.name === 'DEFERRED_REVENUE'
            )?.total || 0
        )

        const recognizedRevenueByMonth = data.period.buckets.map(
          bucket =>
            bucket.recognizedAndDeferred.ledgerAccountTotals.find(
              i => i.name === 'RECOGNIZED_REVENUE'
            )?.total || 0
        )

        return {
          totalDeferred: deferredRevenueByMonth.reduce(floatSafeAdd, 0),
          totalRecognized: recognizedRevenueByMonth.reduce(floatSafeAdd, 0),
          startDate: new Date(data.period.startDate),
          endDate: new Date(data.period.endDate)
        }
      }
    }
  )

  if (chartsDataResponse.isError) {
    throw new Error('Failed to load deferred revenue data')
  }

  if (chartsDataResponse.isPending) {
    return {
      isLoading: true,
      data: null
    }
  }

  invariant(
    chartsDataResponse.data,
    'Revenue recognition stats data should be defined, if loaded'
  )

  return {
    data: {
      totalDeferred: toMoney({
        value: `${chartsDataResponse.data.totalDeferred}`,
        currency: currency ?? 'GBP'
      }),
      totalRecognized: toMoney({
        value: `${chartsDataResponse.data.totalRecognized}`,
        currency: currency ?? 'GBP'
      }),
      startDate: chartsDataResponse.data.startDate,
      endDate: chartsDataResponse.data.endDate
    },
    isLoading: false
  }
}

export const RevRecDashboardStatsCard = (props: {
  hasResults: boolean
}) => {
  const { currency } = useSelectedCurrency()
  const { dateRange } = useSelectedDateRange()

  const [searchParams] = useSearchParams()
  const customerIds = searchParams.get('customerId')
  const productIds = searchParams.get('productId')

  const { isLoading, data } = useRevRecDashboardStatsCard({
    currency,
    dateRange,
    customerIds: customerIds ? customerIds.split(',') : undefined,
    productIds: productIds ? productIds.split(',') : undefined
  })

  if (isLoading) {
    return (
      <Flex p="16px" borderBottom="1px" borderColor={GreyGrey30}>
        <Skeleton height="96px" width="100%" borderRadius={8} />
      </Flex>
    )
  }

  if (!props.hasResults) {
    return null
  }

  return (
    <Flex p="16px" borderBottom="1px" borderColor={GreyGrey30}>
      <Flex
        height="96px"
        width="100%"
        borderRadius={8}
        backgroundColor={GreyGrey10}
        flexDirection="column"
        alignContent="center"
        alignItems="center"
        justifyContent="center"
        gap="8px"
      >
        <Text {...Lato16Bold}>
          {format(data?.startDate, 'd MMM yyyy')} -{' '}
          {format(data?.endDate, 'd MMM yyyy')}
        </Text>
        <Flex alignItems="center" gap="4px" {...Lato13Regular}>
          <Text>Recognized:</Text>
          <Text fontWeight="semibold">{data?.totalRecognized}</Text>
          <Text marginX="4px">·</Text>
          <Text>Deferred:</Text>
          <Text fontWeight="semibold">{data?.totalDeferred}</Text>
        </Flex>
      </Flex>
    </Flex>
  )
}
